import { GridMode, RowWithMetadata, SetRowDataFn } from '../../../shared/gridEdit/useEditableGridLogic';
import type { ScenarioObligation, ScenariosObligationsListLookupData } from '../../../shared/queries/scenarios';
import {
  adminColumnDefinitionFactory,
  buildEditableGridColumnDefinitions,
  ColumnDefinitionConfig,
} from '../../../shared/gridEdit/columnDefinitionsUtils';
import {
  companyCodeColumnDefinition,
  countryCodeColumnDefinition,
  countryTaxTypeColumnDefinition,
  federalFTCLimitColumnDefinition,
  NOLColumnDefinition,
  overrideDeferredTaxRateColumnDefinition,
  overrideTaxCurrencyColumnDefinition,
  overrideTaxRateColumnDefinition,
  recordVAColumnDefinition,
  stateApportionmentRateColumnDefinition,
  stateMinimumTaxColumDefinition,
  statePrePostNOLColumnDefinition,
} from '../../../shared/columnDefinitions';
import { decodeCountryTaxType, encodeCountryTaxType } from '../../../utils/encodeDecodeCTT';
import { floatParser, nullableFloatParser } from '../../../shared/gridEdit/utils/parsers';
import { countryCodes, NOLValues } from '../../../administration/obligationsPage/constants';
import {
  areStateFieldsEditable,
  areUSFederalFieldsEditable,
  isNOLFieldEditable,
  isStatePrePostNOLFieldEditable,
} from '../../../administration/obligationsPage/columnDefinitionsForEditView';
import { YesNoFlag } from '../../../shared/types';
import { getScenarioObligationUniqueId } from './utils';
import ScenarioObligationAdminColumnRenderer from './ScenarioObligationAdminColumnRenderer.component';

const baseColumnDefinitions: ColumnDefinitionConfig<ScenarioObligation, ScenariosObligationsListLookupData>[] = [
  {
    baseColumnDefinition: companyCodeColumnDefinition,
    editableWhen: [GridMode.Add],
    withDropdown: true,
    withDropdownConfig: {
      optionsGetter: context => context.companyCodeOptions,
    },
  },
  {
    baseColumnDefinition: countryCodeColumnDefinition,
  },
  {
    baseColumnDefinition: countryTaxTypeColumnDefinition,
    editableWhen: [GridMode.Add],
    editableWhenConfig: {
      valueSetterFactory: ({ lookUpData, setRowData }) => params => {
        const { newValue: encodedCountryTaxType } = params;
        const countryTaxTypeData = lookUpData.countryTaxTypeData[encodedCountryTaxType];
        const taxCurrency = lookUpData.taxCurrencyDefaultCodes[countryTaxTypeData.countryCode].currencyCode;

        return setRowData({
          ...params,
          field: 'countryTaxType',
          batchUpdateData: {
            countryTaxType: countryTaxTypeData.countryTaxType,
            countryCode: countryTaxTypeData.countryCode,
            overrideTaxRate: countryTaxTypeData.taxRate,
            overrideDefTaxRate: countryTaxTypeData.defTaxRate,
            overrideTaxCurrency: taxCurrency,
          },
          batchUpdateChangeTouched: false,
        });
      },
      valueGetter: ({ data }) => encodeCountryTaxType(data.countryCode, data.countryTaxType),
      cellRenderer: ({ value }: { value: string }) => decodeCountryTaxType(value).countryTaxType,
    },
    withDropdown: true,
    withDropdownConfig: {
      optionsGetter: context => context.countryTaxTypesOptions,
    },
  },
  {
    baseColumnDefinition: overrideTaxCurrencyColumnDefinition,
    editableWhen: [GridMode.Add, GridMode.Edit],
    editableWhenConfig: {
      additionalCondition: ({ data, lookUpData }) => {
        if (data.countryCode === countryCodes.US) {
          return false;
        }

        const foundOption = lookUpData.companyCodeOptions.find(option => option.value === data.companyCode);
        return Boolean(foundOption) && foundOption?.subledgerFlag === YesNoFlag.N;
      },
    },
    withDropdown: true,
    withDropdownConfig: {
      optionsGetter: context => context.currenciesOptions,
    },
  },
  {
    baseColumnDefinition: federalFTCLimitColumnDefinition,
    editableWhen: [GridMode.Add, GridMode.Edit],
    editableWhenConfig: {
      additionalCondition: areUSFederalFieldsEditable,
    },
    withDropdown: true,
    withDropdownConfig: {
      optionsGetter: context => context.federalFTCLimitOptions,
    },
  },
  {
    baseColumnDefinition: stateMinimumTaxColumDefinition,
    editableWhen: [GridMode.Add, GridMode.Edit],
    editableWhenConfig: {
      valueParser: nullableFloatParser,
      additionalCondition: areStateFieldsEditable,
    },
  },
  {
    baseColumnDefinition: stateApportionmentRateColumnDefinition,
    editableWhen: [GridMode.Add, GridMode.Edit],
    editableWhenConfig: {
      valueParser: nullableFloatParser,
      additionalCondition: areStateFieldsEditable,
    },
  },
  {
    baseColumnDefinition: statePrePostNOLColumnDefinition,
    editableWhen: [GridMode.Add, GridMode.Edit],
    editableWhenConfig: {
      additionalCondition: isStatePrePostNOLFieldEditable,
    },
    withDropdown: true,
    withDropdownConfig: {
      optionsGetter: context => context.statePrePostNOLOptions,
    },
  },
  {
    baseColumnDefinition: NOLColumnDefinition,
    editableWhen: [GridMode.Add, GridMode.Edit],
    editableWhenConfig: {
      additionalCondition: isNOLFieldEditable,
    },
    withDropdown: true,
    withDropdownConfig: {
      optionsGetter: context => context.NOLOptions,
    },
  },
  {
    baseColumnDefinition: recordVAColumnDefinition,
    editableWhen: [GridMode.Add, GridMode.Edit],
    editableWhenConfig: {
      additionalCondition: ({ data }) => data.NOL === NOLValues.RECORD,
    },
    withDropdown: true,
    withDropdownConfig: {
      optionsGetter: context => context.recordVaOptions,
    },
  },
  {
    baseColumnDefinition: overrideTaxRateColumnDefinition,
    editableWhen: [GridMode.Add, GridMode.Edit],
    editableWhenConfig: {
      valueParser: floatParser,
    },
  },
  {
    baseColumnDefinition: overrideDeferredTaxRateColumnDefinition,
    editableWhen: [GridMode.Add, GridMode.Edit],
    editableWhenConfig: {
      valueParser: floatParser,
    },
  },
];

export const getColumnDefinitions = ({ setRowData, lookUpData, editRow, context }: {
  setRowData: SetRowDataFn<RowWithMetadata<ScenarioObligation>>,
  lookUpData: ScenariosObligationsListLookupData,
  editRow: (data: RowWithMetadata<ScenarioObligation>) => void,
  context: { mode: GridMode, editedRowId: string, isScenarioLocked: boolean, }
}) => [
  adminColumnDefinitionFactory({
    cellRenderer: ScenarioObligationAdminColumnRenderer,
    editRow,
    context,
  }),
  ...buildEditableGridColumnDefinitions<ScenarioObligation, ScenariosObligationsListLookupData>({
    baseColumnDefinitions,
    lookUpData,
    setRowData,
    getEntityId: getScenarioObligationUniqueId,
  }),
];
